import { trace } from 'owa-trace';
import type { LogData } from 'owa-logging-utils';
import type { MailboxInfo } from 'owa-client-types';
import type { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';

export type SyncEventType =
    | 'Info'
    | 'EngineStateChanged'
    | 'QueueChanged'
    | 'ModuleCompleted'
    | 'ActionQueueChanged';
export type SyncEvent = {
    type: SyncEventType;
    data: any[];
};

export type SyncEventListener = (type: SyncEventType, ...data: any[]) => void;
export type SyncEventsListener = (events: SyncEvent[]) => void;

export type SyncEngineStatus = 'Unknown' | 'NotLeader' | '';

export type SyncQueueEntry = {
    type: string;
    description: string;
};

export type SyncQueueSummary = {
    id?: string | null;
    queue: SyncQueueEntry[];
};

export type SyncModuleSummary = {
    type: string;
    description: string;
    queueTime: number;
    startTime?: number;
    durationMs?: number;
    error?: string;
};

export type ActionQueueEntry = {
    id: number;
    opName: string;
    attempts: number;
    lastAttempt: string;
    isBlocked: boolean;
    lastError?: string;
    lastErrorCount?: number;
    blockingKeys?: string[];
    variables?: Record<string, any>;
    uuid: string;
};

export type ActionQueueSummary = {
    isStarted: boolean;
    isLeader: boolean;
    queue: ActionQueueEntry[];
    running: number[];
};

const listeners: SyncEventListener[] = [];

export function addSyncEventListener(value: SyncEventListener): void {
    listeners.push(value);
}

/**
 * Creates a sync log.
 *
 * Structure the "message" param roughly like the following template for consistency across sync logs:
 * "[Module Name]: [Verb] [Noun] [Additional Info]"
 */
export function emitSyncEvent(
    message: string,
    data: LogData,
    mailboxInfo?: MailboxInfo | typeof INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT | undefined
): void {
    trace.info(`${message} ${data}`, 'offline');
    for (const listener of listeners) {
        listener('Info', Date.now(), message, data, mailboxInfo);
    }
}

export function onSyncEngineStateChanged(state: string): void {
    emitSyncEvent('SyncDiagnostics: engine state changed', { state }, undefined);
    for (const listener of listeners) {
        listener('EngineStateChanged', state);
    }
}

export function onSyncQueueChanged(
    getQueueSummary: () => SyncQueueSummary,
    mailboxInfo: MailboxInfo | typeof INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT
): void {
    // We avoid wasting time examining the queue if there is no listener
    if (listeners.length === 0) {
        return;
    }

    const summary = getQueueSummary();
    for (const listener of listeners) {
        listener('QueueChanged', summary, mailboxInfo);
    }
}

export function onActionQueueChanged(getActionQueueSummary: () => ActionQueueSummary): void {
    for (const listener of listeners) {
        listener('ActionQueueChanged', getActionQueueSummary());
    }
}

export function onSyncModuleCompleted(
    summary: SyncModuleSummary,
    mailboxInfo: MailboxInfo | typeof INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT
): void {
    for (const listener of listeners) {
        listener('ModuleCompleted', summary, mailboxInfo);
    }
}
